export interface KingdomForegroundEffectsProps {
  worldWidth: number;
}

/**
 * L1 idle effects for Greendale Kingdom — three butterflies looping
 * across the foreground at scattered heights. Coordinates are pixel
 * values inside the L1 strip (which is {@code worldWidth} wide).
 *
 * <p>Each butterfly animates on a horizontal sine path via {@code
 * gtFlutter*} keyframes; staggered phase + slightly different speeds
 * give an "actually a few of them, not in formation" feel.
 */
export function KingdomForegroundEffects({
  worldWidth: _worldWidth,
}: KingdomForegroundEffectsProps) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        animation: 'gtFadeIn 300ms ease-out',
      }}
    >
      <Butterfly
        leftStart="14%"
        bottomBase={170}
        hue="#FF7AB6"
        duration="14s"
        delay="0s"
        path="A"
      />
      <Butterfly
        leftStart="48%"
        bottomBase={210}
        hue="#FFC857"
        duration="17s"
        delay="-5s"
        path="B"
      />
      <Butterfly
        leftStart="78%"
        bottomBase={140}
        hue="#9BD8FF"
        duration="13s"
        delay="-9s"
        path="A"
      />
    </div>
  );
}

interface ButterflyProps {
  leftStart: string;
  bottomBase: number;
  hue: string;
  duration: string;
  delay: string;
  path: 'A' | 'B';
}

function Butterfly({
  leftStart,
  bottomBase,
  hue,
  duration,
  delay,
  path,
}: ButterflyProps) {
  return (
    <div
      style={{
        position: 'absolute',
        left: leftStart,
        bottom: bottomBase,
        // Butterflies bumped from 18×14 to 28×22 so the silhouette
        // reads at default zoom; previous size disappeared into the
        // foreground grass strip on a phone-sized viewport.
        width: 28,
        height: 22,
        animation: `${path === 'A' ? 'gtFlutterA' : 'gtFlutterB'} ${duration} ease-in-out ${delay} infinite`,
        willChange: 'transform',
        pointerEvents: 'none',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
      }}
    >
      {/* Body */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 4,
          height: 14,
          marginLeft: -2,
          marginTop: -7,
          background: 'rgba(40,30,30,0.92)',
          borderRadius: 2,
          zIndex: 2,
        }}
      />
      {/* Wings — two ellipses pulsing via gtWingFlap to fake flapping */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: '20%',
          width: '45%',
          height: '60%',
          background: hue,
          borderRadius: '60% 30% 60% 30%',
          opacity: 0.85,
          transformOrigin: '100% 50%',
          animation: 'gtWingFlap 220ms ease-in-out infinite',
          boxShadow: 'inset 0 0 4px rgba(255,255,255,0.45)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '20%',
          width: '45%',
          height: '60%',
          background: hue,
          borderRadius: '30% 60% 30% 60%',
          opacity: 0.85,
          transformOrigin: '0% 50%',
          animation: 'gtWingFlap 220ms ease-in-out infinite',
          boxShadow: 'inset 0 0 4px rgba(255,255,255,0.45)',
        }}
      />
    </div>
  );
}
